import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HiOutlineOfficeBuilding, HiOutlineSearch } from "react-icons/hi";
import axios from "axios";
import Cookies from "js-cookie";

const SelectCollege = () => {
  // New state for college selection
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [loadingColleges, setLoadingColleges] = useState(false);

  // State for "Other" college option
  const [showOtherCollege, setShowOtherCollege] = useState(false);
  const [otherCollegeName, setOtherCollegeName] = useState("");

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchColleges();
  }, []);

  // Filter colleges based on search
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredColleges(colleges);
    } else {
      const filtered = colleges.filter((college) =>
        college.collegeName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredColleges(filtered);
    }
  }, [searchTerm, colleges]);

  const fetchColleges = async () => {
    setLoadingColleges(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllColleges`,
      );
      setColleges(response.data.data || []);
      setFilteredColleges(response.data.data || []);
    } catch (err) {
      console.error("Error fetching colleges:", err);
    } finally {
      setLoadingColleges(false);
    }
  };

  // Handle "Other" option selection
  const handleOtherOption = () => {
    setShowOtherCollege(true);
    setSelectedCollege(null);
    setSearchTerm("");
    setDropdownOpen(false);
  };

  // Handle regular college selection
  const handleCollegeSelect = async (college) => {
    setSelectedCollege(college);
    setSearchTerm(college.collegeName);

    try {
      const token = Cookies.get("userToken");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/userAuth/select-college`,
        {
          collegeId: college?._id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        setOtherCollegeName("");
        setShowOtherCollege(false);
        setDropdownOpen(false);

        setTimeout(() => navigate("/"), 3000);
      }
    } catch (err) {
      console.error("Error fetching colleges:", err);
    } finally {
      setLoading(false);
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/40 w-screen h-screen flex items-center justify-center p-2 z-50">
      <div className="p-4 rounded-lg bg-white shadow min-w-[500px]">
        {/* College Selection - Show after email verification */}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-2"
        >
          <label className="block text-sm font-semibold text-gray-700">
            Select Your College <span className="text-red-500">*</span>
          </label>

          {/* Search Input */}
          <div className="relative">
            <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Search for your college..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
            />
          </div>

          {/* Dropdown */}
          {dropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute z-50 w-full max-w-md mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto"
            >
              {loadingColleges ? (
                <div className="p-4 text-center text-gray-500">
                  Loading colleges...
                </div>
              ) : (
                <>
                  {/* "Other" Option */}
                  <button
                    type="button"
                    onClick={handleOtherOption}
                    className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors flex items-center space-x-3 border-b border-gray-100 bg-amber-50/50"
                  >
                    <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">+</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-amber-700">
                        Other (My college is not listed)
                      </p>
                      <p className="text-xs text-gray-500">
                        Select this if your college is not in the list
                      </p>
                    </div>
                  </button>

                  {/* Regular College Options */}
                  {filteredColleges.length > 0 ? (
                    filteredColleges.map((college) => (
                      <button
                        key={college._id}
                        type="button"
                        onClick={() => handleCollegeSelect(college)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center space-x-3 border-b border-gray-100 last:border-0"
                      >
                        <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-800 truncate">
                            {college.collegeName}
                          </p>
                          {college.address && (
                            <p className="text-xs text-gray-500 truncate">
                              {college.address}
                            </p>
                          )}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No colleges found.{" "}
                      <button
                        onClick={handleOtherOption}
                        className="text-amber-600 hover:text-amber-700 font-medium"
                      >
                        Add your college
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* Selected College or Other College Input */}
          <AnimatePresence mode="wait">
            {selectedCollege && (
              <motion.div
                key="selected"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center space-x-2"
              >
                <HiOutlineOfficeBuilding className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Selected: {selectedCollege.collegeName}
                </span>
              </motion.div>
            )}

            {showOtherCollege && (
              <motion.div
                key="other"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 space-y-2"
              >
                <label className="block text-sm font-semibold text-gray-700">
                  Enter Your College Name{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={otherCollegeName}
                  onChange={(e) => setOtherCollegeName(e.target.value)}
                  placeholder="e.g., Stanford University"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  required={showOtherCollege}
                />
                <p className="text-xs text-gray-500 flex items-center space-x-1">
                  <span className="inline-block w-4 h-4 bg-amber-100 rounded-full text-amber-600 text-center">
                    i
                  </span>
                  <span>
                    Your college will be added to our database after
                    verification
                  </span>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div variants={itemVariants} className="mt-3">
          <motion.button
            onClick={handleCollegeSelect}
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full mx-auto bg-gray-700 text-white text-lg font-semibold py-3 rounded-xl hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {loading ? "...submit" : "Submit"}
          </motion.button>
        </motion.div>
      </div>
    </div>,
    document.body,
  );
};

export default SelectCollege;
